<div class="role-edit-page">
  @if (isLoading()) {
    <zag-loading-state [message]="'common.loading' | translate" />
  } @else if (error()) {
    <zag-error-state [message]="error()!" [backLink]="'/settings/roles'" [backText]="'common.back' | translate" />
  } @else {
    <ng-template zagPageActions>
      <button zagButton type="submit" [disabled]="isSaving() || form.invalid" (click)="onSubmit()">
        {{ 'common.save' | translate }}
      </button>
    </ng-template>

    <zag-entity-detail-layout>
      <div slot="sidebar">
        <zag-profile-summary-card
          [avatarName]="roleName()"
          [avatarIcon]="roleIcon()"
          [subtitle]="roleSubtitle()"
          [stats]="roleStats()"
          [quickActionsTitle]="'common.quickActions' | translate"
          [quickActions]="quickActions()"
          [dangerActions]="dangerActions()"
          [dangerZoneTitle]="'common.dangerZone' | translate"
          (quickActionClick)="onQuickAction($event)"
          (dangerActionClick)="onDangerAction($event)"
        />
      </div>

      <form slot="content" [formGroup]="form">
        <zag-accordion>
          <zag-accordion-item [title]="'settings.roles.sections.details' | translate" icon="info" [expanded]="true">
            <div class="role-edit-page__row">
              <zag-form-field
                [label]="'settings.roles.roleId' | translate"
                [error]="getFieldError('id') ?? ''"
                [required]="true"
                forId="roleId"
              >
                <zag-input
                  id="roleId"
                  formControlName="id"
                  [disabled]="!isNew()"
                  [placeholder]="'settings.roles.roleIdPlaceholder' | translate"
                />
              </zag-form-field>
            </div>

            <zag-form-field
              [label]="'settings.roles.roleDescription' | translate"
              [error]="getFieldError('description') ?? ''"
              forId="description"
            >
              <textarea
                id="description"
                class="role-edit-page__textarea"
                formControlName="description"
                rows="3"
                [placeholder]="'settings.roles.roleDescriptionPlaceholder' | translate"
              ></textarea>
            </zag-form-field>
          </zag-accordion-item>

          <zag-accordion-item [title]="'settings.roles.permissions' | translate" icon="key" [badge]="permissionsCount()" [expanded]="true">
            <sc-permission-checklist
              [permissions]="checklistPermissions()"
              [selectedScopes]="selectedPermissionScopes()"
              (selectedScopesChange)="onPermissionSelectionChange($event)"
            />
          </zag-accordion-item>

          @if (!isNew()) {
            <zag-accordion-item
              [title]="'settings.roles.users.title' | translate"
              icon="person"
              [badge]="assignedUsersBadge()"
              [expanded]="true"
            >
              <div class="role-edit-page__section">
                <p class="role-edit-page__section-description">
                  {{ 'settings.roles.users.description' | translate }}
                </p>

                <!-- Add user dropdown -->
                <zag-dropdown
                  [options]="unassignedUserOptions()"
                  [placeholder]="'settings.roles.users.addPlaceholder' | translate"
                  [value]="selectedUserToAdd()"
                  [disabled]="unassignedUserOptions().length === 0"
                  (selectionChange)="onAddUser($event)"
                />
                @if (unassignedUserOptions().length === 0) {
                  <p class="role-edit-page__empty-hint">
                    {{ 'settings.roles.users.noUnassigned' | translate }}
                  </p>
                }

                <!-- Direct assignments -->
                <h4 class="role-edit-page__subsection-title">
                  {{ 'settings.roles.users.direct' | translate }}
                </h4>
                <div class="role-edit-page__list">
                  @if (isLoadingAssignedUsers()) {
                    <div class="role-edit-page__loading">
                      <span zagIcon="sync" class="spinning"></span>
                      <p>{{ 'common.loading' | translate }}</p>
                    </div>
                  } @else {
                    @for (user of effectiveDirectUsers(); track user.id) {
                      <div class="role-edit-page__item" [class.role-edit-page__item--pending]="user.pending">
                        <span zagIcon="person" class="role-edit-page__icon"></span>
                        <div class="role-edit-page__item-info">
                          <span class="role-edit-page__item-name">{{ user.name }}</span>
                          @if (user.email) {
                            <span class="role-edit-page__item-description">{{ user.email }}</span>
                          }
                        </div>
                        @if (user.pending) {
                          <span class="role-edit-page__badge">
                            {{ 'settings.roles.users.pendingBadge' | translate }}
                          </span>
                        }
                        <button
                          type="button"
                          class="role-edit-page__remove-btn"
                          [title]="'common.remove' | translate"
                          (click)="removeAssignedUser(user.id)"
                        >
                          <span zagIcon="close"></span>
                        </button>
                      </div>
                    } @empty {
                      <p class="role-edit-page__empty">
                        {{ 'settings.roles.users.noDirectAssignments' | translate }}
                      </p>
                    }
                  }
                </div>

                <!-- Inherited assignments (read-only) -->
                @if (inheritedAssignedUsers().length > 0) {
                  <h4 class="role-edit-page__subsection-title">
                    {{ 'settings.roles.users.inherited' | translate }}
                  </h4>
                  <div class="role-edit-page__list">
                    @for (user of inheritedAssignedUsers(); track user.id) {
                      <div class="role-edit-page__item role-edit-page__item--readonly">
                        <span zagIcon="person" class="role-edit-page__icon"></span>
                        <div class="role-edit-page__item-info">
                          <span class="role-edit-page__item-name">{{ user.name }}</span>
                          @if (user.email) {
                            <span class="role-edit-page__item-description">{{ user.email }}</span>
                          }
                          <span class="role-edit-page__item-source">
                            {{ 'settings.roles.users.viaGroups' | translate }}:
                            @for (group of user.sourceGroups; track group.id; let last = $last) {
                              <span>{{ group.name }}{{ last ? '' : ', ' }}</span>
                            }
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </zag-accordion-item>
          }
        </zag-accordion>
      </form>
    </zag-entity-detail-layout>

    @if (!isNew() && canAccessAuditLog()) {
      <zag-audit-log-panel
        schema="rbac"
        table="roles"
        [recordId]="roleId()!"
        [open]="auditPanelOpen()"
        (openChange)="auditPanelOpen.set($event)"
      />
    }
  }
</div>
